import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import * as userApi from 'apis/restaurant-api/merchant-admin/user/api';
import { useFetch } from 'common/hooks';

import useMenuData from '../useMenuData';

export const UNKNOWN = Symbol('UNKNOWN');

const useLatestMenuVersion = () => {
  const { menu, items, options } = useMenuData();

  if (!menu) {
    throw new Error('Menu not found, this should not happen!');
  }

  return useMemo(() => {
    const [latestVersion] = [
      ...items.map(item => item.version),
      ...options.map(option => option.version),
      menu.version,
    ].sort((a, b) => b.created_at.$date - a.created_at.$date);
    return latestVersion;
  }, [menu, items, options]);
};

export const resolveAuthorName = async (intl: IntlShape, authorId: string, authorKind: string) => {
  if (authorKind === 'user' || authorKind === 'employee') {
    try {
      const { users } = await userApi.searchUsers(authorId);
      return users.length === 0 ? UNKNOWN : `${users[0].name.first_name} ${users[0].name.last_name}`;
    } catch (error) {
      // Return UNKNOWN on 403 error, because Wolt externals don't have access to the user search API.
      if (error.response?.status === 403) {
        return UNKNOWN;
      }
      throw error;
    }
  }
  if (authorKind === 'kafka') {
    return intl.formatMessage({ id: 'menu.author-kind-kafka' });
  }
  return authorId;
};

const useMenuLastEdited = () => {
  const intl = useIntl();
  const latestVersion = useLatestMenuVersion();

  const authorId = latestVersion?.author.id;
  const authorKind = latestVersion?.author.kind;

  const { data: lastEditedBy } = useFetch(async () => {
    // Still loading latestVersion or there was an error
    if (authorId === undefined || authorKind === undefined) {
      return undefined;
    }

    return resolveAuthorName(intl, authorId, authorKind);
  }, [authorId, authorKind, intl]);

  if (lastEditedBy === undefined || latestVersion === undefined) {
    return undefined;
  }

  const date = Intl.DateTimeFormat(undefined, { month: 'numeric', day: 'numeric' }).format(
    latestVersion.created_at.$date,
  );
  const time = Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: 'numeric' }).format(
    latestVersion.created_at.$date,
  );

  return lastEditedBy === UNKNOWN
    ? intl.formatMessage({ id: 'menu.lastEditedShort' }, { date, time })
    : intl.formatMessage({ id: 'menu.lastEditedBy' }, { author: `${lastEditedBy}`, date, time });
};

export default useMenuLastEdited;
